import React from "react";
import styled from "styled-components";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import { Link } from "gatsby";
import Box from "react-raster";
import { InView } from "react-intersection-observer";
import Container from "../Container";
import { Eyebrow, H2, H3, Copy } from "../Typography";
import Arrow, { ArrowContainer } from "../Arrow";
import isMobile from "../../isMobile";

const Wrapper = styled.div`
  max-width: 100%;
  width: 100%;
  background-color: ${({ theme, $backgroundColour }) =>
    theme.colours[$backgroundColour]};
  margin-bottom: ${({ $hasBottomMargin }) => ($hasBottomMargin ? "100px" : 0)};
`;

const ImageColumn = styled(Box)`
  order: 1;

  @media (${({ theme }) => theme.mq.l}) {
    position: sticky;
    height: 100vh;
    top: 92px;
    order: ${({ $contentAlignment }) => ($contentAlignment === "left" ? 2 : 1)};
  }
`;

const ContentColumn = styled(Box)`
  order: 2;

  @media (${({ theme }) => theme.mq.l}) {
    order: ${({ $contentAlignment }) => ($contentAlignment === "left" ? 1 : 2)};
    margin: 100px 0;
  }
`;

const ContentArea = styled.article`
  opacity: ${({ $inview }) => ($inview === "true" ? 1 : 0)};
  transform: translateY(${({ $inview }) => ($inview === "true" ? 0 : "100px")});
  transition: all 0.3s;
  color: ${({ theme, $foregroundColour }) => theme.colours[$foregroundColour]};
  margin-bottom: 20px;
  margin-top: 20px;

  @media (prefers-reduced-motion) {
    opacity: 1;
    transform: none;
    transition: none;
  }

  @media (${({ theme }) => theme.mq.l}) {
    margin-top: 90px;
    margin-bottom: 90px;
  }
`;

const ImageContainer = styled.div`
  opacity: ${({ $inview }) => ($inview === "true" ? 1 : 0)};
  transform: translateY(${({ $inview }) => ($inview === "true" ? 0 : "100px")});
  transition: all 0.3s;

  @media (prefers-reduced-motion) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

const StyledLink = styled(Link)`
  font-family: ${({ theme }) => theme.fontFamilyMono};
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  display: flex;
  text-decoration: none;
  display: flex;
  align-items: center;
  margin-top: 50px;
  padding-bottom: 20px;
`;

const StyledExternalLink = styled.a`
  font-family: ${({ theme }) => theme.fontFamilyMono};
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  display: flex;
  text-decoration: none;
  display: flex;
  align-items: center;
  margin-top: 50px;
  padding-bottom: 20px;
`;

const Label = styled.span`
  color: ${({ theme, $foregroundColour }) => theme.colours[$foregroundColour]};
  margin-left: 16px;
  margin: 0 0 0 16px;
  padding: 0;
`;

function TwoColumn({
  title,
  backgroundColour,
  layout,
  image,
  contentAreas,
  ctaLabel,
  ctaUrl,
  hideTitle,
  $hasBottomMargin,
}) {
  const isNonDefaultBackgroundColour =
    backgroundColour &&
    backgroundColour !== "default" &&
    backgroundColour !== "alabasterWhite";
  const foregroundColour = isNonDefaultBackgroundColour ? "white" : "black";
  const arrowBackgroundColour =
    foregroundColour === "white" ? "white" : "black";
  const arrowForegroundColour =
    foregroundColour === "white" ? "black" : "white";

  const contentAlignment = layout === "left of image" ? "left" : "right";

  return (
    <Wrapper
      $backgroundColour={backgroundColour}
      $hasBottomMargin={$hasBottomMargin}
    >
      <Container
        styles={`
          align-self: flex-start; // allows stickiness
        `}
        marginLeft={["30px", "30px", contentAlignment === "left" ? "30px" : 0]}
        marginRight={[
          "30px",
          "30px",
          contentAlignment === "right" ? "30px" : 0,
        ]}
      >
        <ImageColumn
          cols={[6, 10, 5]}
          marginLeft={[0, 1, contentAlignment === "left" ? 1 : 0]}
          marginRight={[0, 1, contentAlignment === "right" ? 1 : 0]}
          $contentAlignment={contentAlignment}
        >
          {image && (
            <InView triggerOnce threshold={isMobile() ? 0.1 : 0.3}>
              {({ inView, ref }) => (
                <ImageContainer ref={ref} $inview={inView ? "true" : "false"}>
                  <GatsbyImage image={getImage(image)} alt={image?.title} />
                </ImageContainer>
              )}
            </InView>
          )}
        </ImageColumn>
        <ContentColumn
          cols={[6, 10, 5]}
          $contentAlignment={contentAlignment}
          marginLeft={[0, 1, contentAlignment === "left" ? 1 : 0]}
        >
          {!hideTitle && title && <H2 $colour={foregroundColour}>{title}</H2>}
          {contentAreas.map((contentArea) => (
            <InView
              triggerOnce
              threshold={isMobile() ? 0.1 : 0.75}
              key={contentArea.id}
            >
              {({ inView, ref }) => (
                <ContentArea
                  ref={ref}
                  $inview={inView ? "true" : "false"}
                  $foregroundColour={foregroundColour}
                >
                  {contentArea.eyebrow && (
                    <Eyebrow colour={foregroundColour}>
                      {contentArea.eyebrow}
                    </Eyebrow>
                  )}
                  {contentArea.title && (
                    <H3 $colour={foregroundColour}>{contentArea.title}</H3>
                  )}
                  {contentArea.description && (
                    <Copy colour={foregroundColour}>
                      {contentArea.description}
                    </Copy>
                  )}
                </ContentArea>
              )}
            </InView>
          ))}
          {ctaUrl ? (
            ctaUrl.indexOf("https") > -1 ? (
              <StyledLink to={ctaUrl}>
                <ArrowContainer $backgroundColour={arrowBackgroundColour}>
                  <Arrow
                    colour={arrowForegroundColour}
                    transform="rotate(-90deg)"
                  />
                </ArrowContainer>
                <Label $foregroundColour={foregroundColour}>{ctaLabel}</Label>
              </StyledLink>
            ) : (
              <StyledExternalLink href={ctaUrl} target="_blank">
                <ArrowContainer $backgroundColour={arrowBackgroundColour}>
                  <Arrow
                    colour={arrowForegroundColour}
                    transform="rotate(-90deg)"
                  />
                </ArrowContainer>
                <Label $foregroundColour={foregroundColour}>{ctaLabel}</Label>
              </StyledExternalLink>
            )
          ) : null}
        </ContentColumn>
      </Container>
    </Wrapper>
  );
}

export default TwoColumn;
